/**
 * Tradução dos erros SENA_* vindos do Postgres.
 *
 * Os erros de trigger DEFERIDA (trg_sena_ticket_validate) chegam no COMMIT,
 * sem path GraphQL e sem extensions úteis — sobra só a string da mensagem.
 * Por isso o casamento é por SUBSTRING, e não por código.
 *
 * Ordem importa: SENA_MAX_6_GAMES precisa ser testado antes de
 * SENA_MAX_6_ITEMS? Não — são strings distintas. Mas prefixos que se contêm
 * (se algum dia existirem) devem vir do mais específico para o mais genérico.
 */
const SENA_CODES = [
  'SENA_ALREADY_SETTLED',
  'SENA_BETTING_CLOSED',
  'SENA_EDITION_NEEDS_6_GAMES',
  'SENA_EDITION_NOT_FOUND',
  'SENA_EDITION_NOT_OPEN',
  'SENA_GAMES_NOT_REVIEWED',
  'SENA_GAMESET_FROZEN',
  'SENA_HASH_SECRET_NOT_SET',
  'SENA_INVALID_TRANSITION',
  'SENA_ITEM_GAME_NOT_IN_EDITION',
  'SENA_LOCKOUT_IN_PAST',
  'SENA_MAX_6_GAMES',
  'SENA_MAX_6_ITEMS',
  'SENA_MISSING_RESULTS',
  'SENA_NOT_ACTIVE_MEMBER',
  'SENA_PRIZE_FROZEN',
  'SENA_STAFF_NOT_ELIGIBLE',
  'SENA_TICKET_IMMUTABLE',
  'SENA_TICKET_NEEDS_6_ITEMS',
  'SENA_TOO_MANY_WINNERS',
]

/** Violações de constraint que não têm prefixo SENA_ mas têm causa conhecida. */
const CONSTRAINT_MESSAGES = {
  sena_ticket_one_per_client: 'sena.errors.SENA_ONE_TICKET_PER_CLIENT',
  uk_sena_eg_order: 'sena.errors.UK_SENA_EG_ORDER',
  uk_sena_eg_game: 'sena.errors.UK_SENA_EG_GAME',
  uk_sena_winner: 'sena.errors.UK_SENA_WINNER',
  sena_edition_edition_number_key: 'sena.errors.UK_SENA_EDITION_NUMBER',
  sena_edition_game_game_id_fkey: 'sena.errors.FK_SENA_GAME_IN_USE',
  sena_ticket_item_game_id_fkey: 'sena.errors.FK_SENA_GAME_IN_USE',
}

/**
 * Extrai a mensagem crua de um erro do Apollo, olhando todos os lugares
 * onde o Hasura pode ter deixado o texto do Postgres.
 */
export function rawMessage (error) {
  if (!error) return ''
  const parts = [error.message]
  const gqlErrors = (error.graphQLErrors || [])
  gqlErrors.forEach(e => {
    parts.push(e.message)
    const internal = e.extensions && e.extensions.internal
    if (internal && internal.error) {
      parts.push(internal.error.message, internal.error.description)
    }
  })
  if (error.networkError && error.networkError.result) {
    parts.push(JSON.stringify(error.networkError.result))
  }
  return parts.filter(Boolean).join(' | ')
}

/**
 * Devolve a chave i18n para o erro, ou null se não for um erro conhecido
 * da Sena (aí o chamador usa seu toast genérico de sempre).
 */
export function senaErrorKey (error) {
  const raw = rawMessage(error)
  if (!raw) return null

  const code = SENA_CODES.find(c => raw.includes(c))
  if (code) return `sena.errors.${code}`

  const constraint = Object.keys(CONSTRAINT_MESSAGES).find(c => raw.includes(c))
  if (constraint) return CONSTRAINT_MESSAGES[constraint]

  return null
}

/**
 * Mixin: `this.senaToastError(error, 'chave.de.fallback')`.
 * Erro conhecido vira mensagem em pt-BR; desconhecido cai no fallback.
 */
export const senaErrorMixin = {
  methods: {
    senaToastError (error, fallbackKey = 'toasts.errorUpdate') {
      const key = senaErrorKey(error)
      this.showToast(this.$t(key || fallbackKey), {
        icon: 'fa-exclamation-triangle',
        position: 'top-right',
        duration: key ? 6000 : 2500,
        fullWidth: false,
      })
      if (!key && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sena] erro não mapeado:', rawMessage(error))
      }
    },
    senaToastSuccess (key) {
      this.showToast(this.$t(key), {
        icon: 'fa-check',
        position: 'top-right',
        duration: 2000,
        fullWidth: false,
      })
    },
  },
}

export default senaErrorMixin
